import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import {
  ArrowRight, Play, Zap, Mic, Video, DollarSign,
  TrendingUp, Users, Radio, Music, Sparkles, Bot,
  Eye, Rocket, ChevronRight, Crown, Flame, Trophy
} from 'lucide-react';

// ─── Skeleton Loader ────────────────────────────────
function Skeleton({ className }) {
  return <div className={`animate-pulse bg-muted/30 rounded-xl ${className}`} />;
}

// ─── Live Activity Strip ──────────────────────────────
function LiveActivityStrip({ stats }) {
  const items = [
    { icon: Flame, text: `${stats.viral || 12} creators went viral today`, color: 'text-orange-400' },
    { icon: DollarSign, text: `$${stats.earned || '3,420'} earned by creators today`, color: 'text-emerald-400' },
    { icon: Radio, text: `${stats.live || 8} live rooms happening now`, color: 'text-red-400' },
    { icon: Mic, text: `${stats.uploads || 23} new tracks uploaded in DMV`, color: 'text-cyan-400' },
    { icon: TrendingUp, text: 'Top 20 Charts updated weekly', color: 'text-violet-400' },
    { icon: Users, text: `${stats.users || '1,200'}+ creators on the platform`, color: 'text-amber-400' },
    { icon: Crown, text: 'Enterprise tools now available', color: 'text-yellow-400' },
    { icon: Music, text: `${stats.beats || 45} beats listed on marketplace`, color: 'text-pink-400' },
  ];

  return (
    <div className="relative overflow-hidden bg-muted/20 border-y border-border py-3" data-testid="live-activity-strip">
      <div className="flex animate-marquee gap-8 whitespace-nowrap">
        {[...items, ...items].map((item, i) => (
          <div key={i} className="flex items-center gap-2 flex-shrink-0">
            <item.icon className={`w-4 h-4 ${item.color}`} />
            <span className="text-sm text-muted-foreground">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Feature Card ─────────────────────────────────────
function FeatureCard({ icon: Icon, title, description, color, link, delay }) {
  return (
    <Link to={link} className="block group" data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <Card className="relative p-5 bg-muted/20 border-border hover:border-border transition-all duration-300 h-full overflow-hidden group-hover:translate-y-[-2px] group-hover:shadow-lg">
        <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`} style={{ background: color }} />
        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <h3 className="font-bold text-base mb-1 tracking-tight">{title}</h3>
        <p className="text-sm text-muted-foreground/60 leading-relaxed">{description}</p>
        <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground/70 group-hover:translate-x-1 transition-all mt-3" />
      </Card>
    </Link>
  );
}

// ─── Trending Creator Card ────────────────────────────
function TrendingCard({ artist }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const handle = artist.handle || artist.user_id || artist.artist_id;
  if (!handle) return null;

  const name = artist.name || artist.artist_name || artist.display_name || 'Unknown';
  const hasAvatar = artist.avatar_url && !imgError;

  // Role-based gradient fallback
  const roleGradients = {
    artist: 'from-violet-700/40 to-indigo-900/60',
    producer: 'from-cyan-700/40 to-blue-900/60',
    dj: 'from-rose-700/40 to-pink-900/60',
    default: 'from-slate-700/40 to-zinc-900/60',
  };
  const grad = roleGradients[artist.role] || roleGradients.default;

  return (
    <Link
      to={`/u/${handle}`}
      className="flex-shrink-0 w-40 sm:w-44 group"
      data-testid={`trending-card-${handle}`}
    >
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-muted/30">
        {/* Skeleton placeholder */}
        {hasAvatar && !imgLoaded && (
          <div className="absolute inset-0 animate-pulse bg-muted/40" />
        )}

        {/* Image or fallback */}
        {hasAvatar ? (
          <img
            src={artist.avatar_url}
            alt={name}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-b ${grad}`}>
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-2">
              <span className="text-2xl font-bold text-white/70">{name[0]?.toUpperCase()}</span>
            </div>
            <Music className="w-5 h-5 text-white/20" />
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

        {/* Info */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="font-bold text-sm truncate text-white">{name}</p>
          <p className="text-[11px] text-white/50 truncate">@{artist.handle || handle}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground/60 px-0.5">
        {(artist.followers_count > 0 || artist.total_plays > 0) ? (
          <>
            {artist.followers_count > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> {artist.followers_count.toLocaleString()}
              </span>
            )}
            {artist.total_plays > 0 && (
              <span className="flex items-center gap-1">
                <Play className="w-3 h-3" /> {artist.total_plays.toLocaleString()}
              </span>
            )}
          </>
        ) : (
          <span className="flex items-center gap-1">
            <Flame className="w-3 h-3 text-orange-400/60" /> Rising
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Main Homepage ────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [topArtists, setTopArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const trendingRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chartsRes, statsRes] = await Promise.all([
          fetch(`${API}/charts/top-20-artists`).catch(() => null),
          fetch(`${API}/stats/platform`).catch(() => null),
        ]);
        if (chartsRes?.ok) {
          const data = await chartsRes.json();
          setTopArtists(data.slice(0, 10));
        }
        if (statsRes?.ok) {
          const data = await statsRes.json();
          setStats(data);
        }
      } catch {} finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="homepage">
      {/* ──── Hero Section ──── */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-600/10 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-orange-500/5 blur-[100px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 pt-20 pb-16 md:pt-32 md:pb-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border mb-6" data-testid="hero-badge">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-muted-foreground/80">Live Platform &mdash; DMV Creators Active Now</span>
            </div>

            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-5"
              style={{ fontFamily: 'Barlow Condensed, system-ui, sans-serif' }}
              data-testid="hero-headline"
            >
              <span className="text-foreground">Create. Go Viral.</span>
              <br />
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                Get Paid.
              </span>
              <br />
              <Link to="/charts" className="text-foreground text-2xl sm:text-3xl lg:text-4xl font-bold tracking-wide hover:text-primary transition-colors cursor-pointer underline decoration-primary/30 underline-offset-4">
                Top 20 DMV Charts for all creators!
              </Link>
              <br />
              <span className="text-foreground">All in One Place.</span>
            </h1>

            {/* Subtext */}
            <p className="text-base md:text-lg text-muted-foreground/60 max-w-xl mx-auto mb-8 leading-relaxed" data-testid="hero-subtext">
              DMV Soundstage is the all-in-one platform for creators to build, promote, and monetize their content.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="hero-ctas">
              <Button
                size="lg"
                className="bg-violet-600 hover:bg-violet-700 rounded-full px-8 gap-2 text-base font-bold shadow-lg shadow-violet-600/25"
                onClick={() => navigate(isAuthenticated ? '/feed' : '/login')}
                data-testid="cta-start-creating"
              >
                <Sparkles className="w-5 h-5" /> Start Creating
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 gap-2 text-base border-border hover:bg-muted/30"
                onClick={() => navigate('/feed')}
                data-testid="cta-explore-trending"
              >
                <TrendingUp className="w-5 h-5" /> Explore Trending
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 gap-2 text-base border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                onClick={() => navigate('/charts')}
                data-testid="cta-view-charts"
              >
                <Trophy className="w-5 h-5" /> View Charts
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ──── Live Activity Strip ──── */}
      <LiveActivityStrip stats={stats} />

      {/* ──── Trending Creators ──── */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-16" data-testid="trending-section">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui, sans-serif' }}>
              Trending in DMV
            </h2>
            <p className="text-sm text-muted-foreground/60 mt-1">Rising creators making waves</p>
          </div>
          <Link to="/discover" className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1">
            See All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="flex gap-3 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-40 sm:w-44">
                <Skeleton className="aspect-[3/4] rounded-2xl mb-2" />
                <Skeleton className="h-3 w-24 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={trendingRef}
            className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {topArtists.map((artist, i) => (
              <div key={artist.user_id || i} className="snap-start flex-shrink-0">
                <TrendingCard artist={artist} />
              </div>
            ))}
            {topArtists.length === 0 && (
              <p className="text-sm text-muted-foreground/50 py-8 text-center w-full">No trending creators yet. Be the first!</p>
            )}
          </div>
        )}
      </section>

      {/* ──── Feature Highlight Cards ──── */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-16" data-testid="features-section">
        <div className="text-center mb-10">
          <h2 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui, sans-serif' }}>
            Everything You Need to Win
          </h2>
          <p className="text-sm text-muted-foreground/60 mt-2">Powerful tools built for creators</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            icon={Rocket} title="Viral Engine" description="Know before you post — get viral scores, best timing, and audience match"
            color="#F97316" link="/viral-engine"
          />
          <FeatureCard
            icon={DollarSign} title="Monetization" description="Turn content into income — tips, subscriptions, locks, and bookings"
            color="#10B981" link="/creator/earnings"
          />
          <FeatureCard
            icon={Crown} title="Gamification" description="Earn XP, level up, unlock badges, and climb the leaderboard"
            color="#F59E0B" link="/leaderboard"
          />
          <FeatureCard
            icon={Trophy} title="Top 20 Charts" description="See who's running the DMV — weekly rankings across all creator roles"
            color="#A855F7" link="/charts"
          />
        </div>
      </section>

      {/* ──── Final CTA ──── */}
      <section className="relative overflow-hidden py-16 md:py-24" data-testid="final-cta-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[120px]" />
        </div>
        <div className="relative max-w-xl mx-auto text-center px-4">
          <h2
            className="text-2xl md:text-4xl font-black tracking-tight mb-4"
            style={{ fontFamily: 'Barlow Condensed, system-ui, sans-serif' }}
          >
            Ready to take over your lane?
          </h2>
          <p className="text-muted-foreground/60 mb-8 text-base">
            Join thousands of DMV creators already building their future on Soundstage.
          </p>
          <Button
            size="lg"
            className="bg-violet-600 hover:bg-violet-700 rounded-full px-10 gap-2 text-base font-bold shadow-lg shadow-violet-600/25"
            onClick={() => navigate(isAuthenticated ? '/feed' : '/login')}
            data-testid="cta-join"
          >
            Join DMV Soundstage <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* ──── Footer ──── */}
      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground/50">DMV Soundstage &copy; {new Date().getFullYear()}</p>
          <div className="flex items-center gap-6 text-xs text-muted-foreground/50">
            <Link to="/feed" className="hover:text-muted-foreground/70 transition-colors">Feed</Link>
            <Link to="/discover" className="hover:text-muted-foreground/70 transition-colors">Discover</Link>
            <Link to="/leaderboard" className="hover:text-muted-foreground/70 transition-colors">Leaderboard</Link>
            <Link to="/charts" className="hover:text-muted-foreground/70 transition-colors">Charts</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
